import React, { useState, useEffect } from "react";
import arcBridge from 'frontend/js/arcBridge';

export const Orders = () => {
  const [loading, setLoading] = useState(true);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    async function getOrders () {
      try {
        const response = await arcBridge.getOrders();
        if (response.success) {
          // console.log("Orders fetched successfully:", response.transactions);
          const sortedOrders = (response.transactions || []).sort((a, b) => {
            const dateOf = (o: any) => {
              const d = o?.sale_date || o?.created_at;
              return d ? new Date(d).getTime() : 0;
            };
            const dateDiff = dateOf(b) - dateOf(a);
            if (dateDiff !== 0) return dateDiff;

            const idOf = (o: any) => (o?.order_number ?? o?.number ?? '').toString();
            const numOf = (id: string) => {
              const digits = id.replace(/[^0-9]/g, '');
              return digits ? parseInt(digits, 10) : NaN;
            };

            const aNum = numOf(idOf(a));
            const bNum = numOf(idOf(b));
            if (!Number.isNaN(bNum) && !Number.isNaN(aNum) && aNum !== bNum) {
              return bNum - aNum;
            }
            return 0;
          });
          setOrders(sortedOrders);
        } else {
          console.error("Failed to fetch orders:", response.message);
        }
      } catch (error) {
        console.error("Failed to fetch orders", error);
      } finally {
        setLoading(false);
      }
    }
    getOrders();
  }, [])

  const handleViewDetails = (order) => {
    const orderId = order.id || order.order_number || order.number;
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const isOrderExpanded = (order) => {
    const orderId = order.id || order.order_number || order.number;
    return expandedOrderId === orderId;
  };

  const getDisplayOrderId = (o: any) => {
    const storeName = (o?.store_name || '').toString().trim().toLowerCase();
    // console.log("Store name:", storeName);
    const isOnline = storeName === 'online store';
    // If online store, prefer Shopify order_number; if in-store, prefer docket number (number)
    return isOnline ? (o?.order_number || o?.number || 'N/A') : (o?.number || o?.order_number || 'N/A');
  };

  return (
    <div>
      {loading ? (
        <span className="text-gray-400">Loading...</span>
      ) : (
        <div>
          {orders.length === 0 ? (
            <div className="pt-8 lg:pt-10 text-[#54565A]">
              <p className="mb-5 lg:mb-10 b1">No orders found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto account-orders__history">
              <table className="min-w-full lg:w-[850px]">
                <thead className="bg-white border-b-2 border-b-[#54565A] max-lg:hidden">
                  <tr>
                    <th scope="col" className="font-normal text-left b3">Order ID</th>
                    <th scope="col" className="font-normal text-left b3">Payment Date</th>
                    <th scope="col" className="font-normal text-left b3">Payment Method</th>
                    <th scope="col" className="font-normal text-left b3">Amount</th>
                    <th scope="col" className="font-normal text-left b3">Location</th>
                    <th scope="col" className="font-normal text-left b3 lg:text-right">Items</th>
                  </tr>
                </thead>
                <tbody className="">
                  {orders.map((order, index) => (
                    <React.Fragment key={order.id || index}>
                      <tr 
                        className={`lg:table-row max-lg:flex flex-col lg:flex-row cursor-pointer ${
                          isOrderExpanded(order) ? '' : 'lg:border-b-2 lg:border-b-[#ECEBEB]'
                        }`}
                        onClick={() => handleViewDetails(order)}
                      >
                        <td className="items-start border-b-2 whitespace-nowrap b3 border-b-gray-100 lg:border-none max-lg:flex">
                          <span className="w-1/2 lg:hidden b3">Order ID</span>
                          <span className="b3">{getDisplayOrderId(order)}</span>
                        </td>
                        <td className="items-start border-b-2 whitespace-nowrap b3 border-b-gray-100 lg:border-none max-lg:flex">
                          <span className="w-1/2 lg:hidden b3">Payment Date</span>
                          <span className="b3">
                            {order.sale_date ? new Date(order.sale_date).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            }) :
                             order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            }) : 'N/A'}
                          </span>
                        </td>
                        <td className="items-start border-b-2 whitespace-nowrap b3 border-b-gray-100 lg:border-none max-lg:flex">
                          <span className="w-1/2 lg:hidden b3">Payment Method</span>
                          <span className="b3">
                            {order.payment_method || order.gateway_display_name || 'N/A'}
                          </span>
                        </td>
                        <td className="items-start border-b-2 whitespace-nowrap b3 border-b-gray-100 lg:border-none max-lg:flex">
                          <span className="w-1/2 lg:hidden b3">Amount</span>
                          <span className="b3">
                            {order.currency?.code
                              ? `$${parseFloat(order.total_amount).toFixed(2)}`
                              : `$${order.total_amount || 0}`}
                          </span>
                        </td>
                        <td className="items-start border-b-2 whitespace-nowrap b3 !text-[#36573B] border-b-gray-100 lg:border-none max-lg:flex">
                          <span className="w-1/2 lg:hidden b3">Location</span>
                          <span className="b3">
                            {order.store_name || 'N/A'}
                          </span>
                        </td>
                        <td className="items-start justify-between border-none whitespace-nowrap b3 lg:text-right max-lg:flex">
                          <span className="lg:hidden b3"></span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(order);
                            }}
                            className="text-[#00263E] underline text-sm/[18px] p-0 normal-case font-normal w-auto min-h-[auto] h-auto tracking-normal"
                          >
                            {isOrderExpanded(order) ? 'Close' : (() => {
                              const itemCount = order.details?.length || order.line_items?.length || order.items?.length || 0;
                              return `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
                            })()}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded order details row */}
                      {isOrderExpanded(order) && (
                        <tr>
                          <td colSpan={6} className="p-4 border-b-2 border-[#ECEBEB]">
                            <div className="space-y-4">
                              <div className="space-y-4">
                                {(order.details || order.line_items || []).map((item, itemIndex) => (
                                  <div key={itemIndex} className="flex items-start space-x-4 pb-4 lg:space-x-[54px] bg-white border-b border-[#ECEEEF] last:border-b-0">
                                    <div className="flex-shrink-0">
                                      {item.url ? (
                                        <img 
                                          src={item.url} 
                                          alt={item.product_name || item.name || 'Product'} 
                                          className="object-cover w-16 h-24"
                                        />
                                      ) : (
                                        <div className="flex items-center justify-center w-16 h-24 bg-gray-200">
                                          <span className="text-xs text-gray-400">No image</span>
                                        </div>
                                      )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm/[22px] text-[#54565A] truncate font-body lg:mb-[19px]">
                                        {item.product_name || item.name || 'N/A'}
                                      </p>
                                      <p className="text-sm/[22px] text-[#54565A]">
                                        {item.sku || item.variant_sku || item.product_code || 'N/A'}
                                      </p>
                                    </div>

                                    <div className="text-right">
                                      <p className="text-sm/[22px] text-[#54565A]">
                                        $ {parseFloat(item.value || (item.price * (item.quantity || 1)) || 0).toFixed(2)}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* No items message */}
                              {(!order.details && !order.line_items) || (order.details && order.details.length === 0) || (order.line_items && order.line_items.length === 0) ? (
                                <div className="py-4 text-center">
                                  <p className="text-gray-500">No items found for this order.</p>
                                </div>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              <div className="mt-6 lg:mt-14">
                <a
                  href="/account?view=orders"
                  className="flex items-center gap-2 text-black hover:no-underline"
                >
                  See online orders
                  <i className="text-2xl text-[#00263E] icon-chevron-right"></i>
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Orders;